import { useMemo } from 'react';
import produce from 'immer';
import { gql } from '@soundxyz/gql-string';
import type { Maybe } from '@soundxyz/utils';
import { useAuth } from '@context/auth/base';
import {
  FollowerUsersDocument,
  FollowingUsersDocument,
  FollowStatusDocument,
  setQueryData,
  useInfiniteQuery,
  useQuery,
} from '@graphql/reactQuery';
import { createBatchingInputStore } from '@store/batchInput';

/*#__PURE__*/ gql(/* GraphQL */ `
  query FollowStatus($users: [UUID!]!) {
    userRelationStatuses(users: $users) {
      isFollowing
      userId
    }
  }

  query FollowingUsers($publicAddress: Address!, $followingCursor: CursorConnectionArgs!) {
    userByAddress(publicAddress: $publicAddress) {
      id
      following(pagination: $followingCursor) {
        edges {
          node {
            id
            userB {
              id
            }
            ...FollowingModalRelation
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }

  query FollowerUsers($publicAddress: Address!, $followingCursor: CursorConnectionArgs!) {
    userByAddress(publicAddress: $publicAddress) {
      id
      followers(pagination: $followingCursor) {
        edges {
          node {
            id
            userA {
              id
            }
            ...FollowersModalRelation
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`);

const { batchedKeys, useBatchedKey } = createBatchingInputStore({
  chunkLimit: 50,
});

export function setFollowingStatus({
  userId,
  isFollowing,
}: {
  userId: string;
  isFollowing: boolean;
}) {
  const users = batchedKeys.keys.get(userId);

  if (!users) {
    return;
  }

  setQueryData(
    {
      query: FollowStatusDocument,
      variables: {
        users,
      },
    },
    previousData => {
      if (!previousData) return previousData;

      return produce(previousData, draft => {
        for (const value of draft.data.userRelationStatuses) {
          if (value.userId === userId) {
            value.isFollowing = isFollowing;
            return;
          }
        }

        draft.data.userRelationStatuses.push({
          isFollowing,
          userId,
        });
      });
    },
  );
}

export function useFollowingStatus({ userId }: { userId: Maybe<string> }) {
  const users = useBatchedKey({ key: userId });

  const setHasUserId = !!users;

  const { isAuthenticatedWithAPI } = useAuth();

  const { data: followStatusData = false, isLoading } = useQuery(FollowStatusDocument, {
    variables: !!users && {
      users,
    },
    cacheTime: Infinity,
    select({ data }) {
      for (const statusData of data.userRelationStatuses) {
        if (statusData.userId === userId) return statusData.isFollowing;
      }

      return false;
    },
    enabled: isAuthenticatedWithAPI && !!users?.length,
    keepPreviousData: true,
    staleTime: 0,
  });

  const followStatus = useMemo(() => {
    if (!isAuthenticatedWithAPI) return false;

    // If the set still doesn't have the user id, we assume it's waiting for the batch
    if (isLoading || !setHasUserId) return 'loading';

    return followStatusData;
  }, [setHasUserId, isLoading, followStatusData, isAuthenticatedWithAPI]);

  return followStatus;
}

export function useFollowersFollowing({ publicAddress }: { publicAddress: Maybe<string> }) {
  const {
    orderedList: followingUsersData,
    isLoadingNewPage: followingLoading,
    loadMoreNextPage: followingLoadMoreNextPage,
  } = useInfiniteQuery(FollowingUsersDocument, {
    getNextPageParam({ data: { userByAddress } }) {
      return (
        userByAddress?.following.pageInfo.hasNextPage && {
          after: userByAddress.following.pageInfo.endCursor,
        }
      );
    },
    list({ userByAddress }) {
      return userByAddress?.following.edges.map(edge => edge.node);
    },
    uniq({ id }) {
      return id;
    },
    filterQueryKey: {
      publicAddress,
    },
    variables:
      !!publicAddress &&
      (({ pageParam }) => {
        return {
          publicAddress,
          followingCursor: {
            first: 20,
            after: pageParam?.after || null,
          },
        };
      }),
    staleTime: 0,
  });

  const {
    orderedList: followerUsersData,
    isLoadingNewPage: followersLoading,
    loadMoreNextPage: followersLoadMoreNextPage,
  } = useInfiniteQuery(FollowerUsersDocument, {
    getNextPageParam({ data: { userByAddress } }) {
      return (
        userByAddress?.followers.pageInfo.hasNextPage && {
          after: userByAddress.followers.pageInfo.endCursor,
        }
      );
    },
    list({ userByAddress }) {
      return userByAddress?.followers.edges.map(edge => edge.node);
    },
    uniq({ id }) {
      return id;
    },
    filterQueryKey: { publicAddress },
    variables:
      !!publicAddress &&
      (({ pageParam }) => {
        return {
          publicAddress,
          followingCursor: {
            first: 20,
            after: pageParam?.after || null,
          },
        };
      }),
    staleTime: 0,
  });

  return {
    followingLoading,
    followingUsersData,
    followersLoading,
    followerUsersData,
    followingLoadMore: followingLoadMoreNextPage,
    followersLoadMore: followersLoadMoreNextPage,
  };
}
